/**
 * WebsitePreview - Component for showing website generation progress and preview
 * 
 * Shows a preview of the website being generated with:
 * - Step indicators (content generation → image generation → complete)
 * - Iframe preview that loads as content becomes available
 * - Link to open the full website when complete
 */

import React, { FC, useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Progress,
  Button,
  Badge,
  Spinner,
  Icon,
  Link,
  useColorModeValue,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';

interface WebsitePreviewProps {
  status?: 'pending' | 'generating_content' | 'generating_images' | 'complete' | 'failed';
  domain?: string;
  url?: string;
}

// Step configuration
const STEPS = [
  { key: 'generating_content', label: 'Creating Content', description: 'Generating text and layout...' },
  { key: 'generating_images', label: 'Creating Images', description: 'Generating images with AI...' },
  { key: 'complete', label: 'Complete', description: 'Your website is ready!' },
];

const CheckIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.3332 4L5.99984 11.3333L2.6665 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StepIndicator: FC<{
  step: typeof STEPS[0];
  index: number;
  currentStepIndex: number;
  isComplete: boolean;
  isFailed: boolean;
}> = ({ step, index, currentStepIndex, isComplete, isFailed }) => {
  const isActive = index === currentStepIndex;
  const isPast = index < currentStepIndex || isComplete;
  
  // Colors
  const activeColor = 'orange.500';
  const completedColor = 'green.500';
  const inactiveColor = 'gray.300';
  const failedColor = 'red.500';
  
  const getColor = () => {
    if (isFailed && isActive) return failedColor;
    if (isPast) return completedColor;
    if (isActive) return activeColor;
    return inactiveColor;
  };
  
  return (
    <Flex align="center" gap={3}>
      {/* Step circle */}
      <Flex
        align="center"
        justify="center"
        w="32px"
        h="32px"
        borderRadius="full"
        bg={isPast ? completedColor : 'white'}
        border="2px solid"
        borderColor={getColor()}
        transition="all 0.3s ease"
      >
        {isPast ? (
          <Box color="white">
            <CheckIcon />
          </Box>
        ) : isActive ? (
          isFailed ? (
            <Text color={failedColor} fontWeight="bold">✕</Text>
          ) : (
            <Spinner size="sm" color={activeColor} />
          )
        ) : (
          <Text fontSize="sm" fontWeight="bold" color={inactiveColor}>
            {index + 1}
          </Text>
        )}
      </Flex>
      
      {/* Step text */}
      <VStack align="start" spacing={0}>
        <Text
          fontSize="sm"
          fontWeight={isActive || isPast ? 'semibold' : 'normal'}
          color={isActive ? 'gray.800' : isPast ? 'green.600' : 'gray.400'}
          fontFamily="'Noto Sans', 'Open Sans', sans-serif"
        >
          {step.label}
        </Text>
        {isActive && (
          <Text
            fontSize="xs"
            color={isFailed ? 'red.500' : 'gray.500'}
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
          >
            {isFailed ? 'An error occurred' : step.description}
          </Text>
        )}
      </VStack>
    </Flex>
  );
};

const WebsitePreview: FC<WebsitePreviewProps> = ({
  status = 'pending',
  domain = '',
  url = '',
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  
  // Parse props if they're strings
  const parsedStatus = status as WebsitePreviewProps['status'];
  const parsedDomain = String(domain);
  const parsedUrl = String(url);
  
  // Determine current step index
  const getCurrentStepIndex = () => {
    switch (parsedStatus) {
      case 'pending':
        return -1;
      case 'generating_content':
        return 0;
      case 'generating_images':
        return 1;
      case 'complete':
        return 2;
      case 'failed':
        return 0; // Show as failed on current step
      default:
        return -1;
    }
  };
  
  const currentStepIndex = getCurrentStepIndex();
  const isComplete = parsedStatus === 'complete';
  const isFailed = parsedStatus === 'failed';
  
  // Calculate progress percentage
  const progressPercent = isComplete ? 100 : ((currentStepIndex + 1) / STEPS.length) * 100;
  
  // Handle iframe load
  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };
  
  // Only show iframe when we have content
  const showIframe = (parsedStatus === 'generating_images' || isComplete) && parsedUrl;
  
  // Border colors
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.800');

  return (
    <Box
      borderRadius="xl"
      bg={bgColor}
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      maxW="650px"
      boxShadow="lg"
    >
      {/* Header with domain link */}
      {parsedDomain && (
        <Flex
          align="center"
          justify="space-between"
          px={4}
          py={3}
          bg="gray.50"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <HStack spacing={2}>
            <Box w="12px" h="12px" borderRadius="full" bg="red.400" />
            <Box w="12px" h="12px" borderRadius="full" bg="yellow.400" />
            <Box w="12px" h="12px" borderRadius="full" bg="green.400" />
          </HStack>
          <Flex
            align="center"
            bg="white"
            px={4}
            py={1.5}
            borderRadius="full"
            border="1px solid"
            borderColor="gray.200"
            flex="1"
            mx={4}
          >
            <Text
              fontSize="sm"
              color="gray.500"
              fontFamily="monospace"
              isTruncated
            >
              https://{parsedDomain}
            </Text>
          </Flex>
          {isComplete && parsedUrl && (
            <Link href={parsedUrl} isExternal _hover={{ textDecoration: 'none' }}>
              <Button
                size="sm"
                colorScheme="orange"
                rightIcon={<ExternalLinkIcon />}
                borderRadius="lg"
                fontFamily="'Noto Sans', 'Open Sans', sans-serif"
              >
                Open
              </Button>
            </Link>
          )}
        </Flex>
      )}

      {/* Progress section */}
      <Box p={5}>
        {/* Progress bar */}
        <Progress
          value={progressPercent}
          size="sm"
          colorScheme={isFailed ? 'red' : isComplete ? 'green' : 'orange'}
          borderRadius="full"
          mb={5}
          isAnimated={!isComplete && !isFailed}
          hasStripe={!isComplete && !isFailed}
        />

        {/* Steps */}
        <VStack spacing={4} align="stretch" mb={5}>
          {STEPS.map((step, index) => (
            <StepIndicator
              key={step.key}
              step={step}
              index={index}
              currentStepIndex={currentStepIndex}
              isComplete={isComplete}
              isFailed={isFailed}
            />
          ))}
        </VStack>

        {/* Status badge */}
        <Flex justify="center" mb={4}>
          {isFailed ? (
            <Badge colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="full">
              Generation encountered an issue - We&apos;ll contact you with results
            </Badge>
          ) : isComplete ? (
            <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
              🎉 Your website is live!
            </Badge>
          ) : (
            <Badge colorScheme="orange" fontSize="sm" px={3} py={1} borderRadius="full">
              Creating your website...
            </Badge>
          )}
        </Flex>
      </Box>

      {/* Preview iframe */}
      {showIframe && (
        <Box
          position="relative"
          h={showFullPreview ? '500px' : '300px'}
          transition="height 0.3s ease"
          borderTop="1px solid"
          borderColor={borderColor}
        >
          {!iframeLoaded && (
            <Flex
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              align="center"
              justify="center"
              bg="gray.50"
              zIndex={1}
            >
              <VStack spacing={3}>
                <Spinner size="lg" color="orange.500" />
                <Text fontSize="sm" color="gray.500">Loading preview...</Text>
              </VStack>
            </Flex>
          )}
          <iframe
            ref={iframeRef}
            src={parsedUrl}
            title="Website Preview"
            width="100%"
            height="100%"
            style={{
              border: 'none',
              transform: 'scale(0.75)',
              transformOrigin: 'top left',
              width: '133.33%',
              height: '133.33%',
            }}
            onLoad={handleIframeLoad}
          />
          
          {/* Expand/collapse button */}
          <Button
            position="absolute"
            bottom="10px"
            right="10px"
            size="sm"
            variant="solid"
            bg="white"
            boxShadow="md"
            onClick={() => setShowFullPreview(!showFullPreview)}
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
          >
            {showFullPreview ? 'Show less' : 'Show more'}
          </Button>
        </Box>
      )}

      {/* Footer with CTA */}
      {isComplete && parsedUrl && (
        <Flex
          p={4}
          bg="orange.50"
          borderTop="1px solid"
          borderColor="orange.100"
          align="center"
          justify="center"
          gap={2}
        >
          <Text
            fontSize="sm"
            color="orange.700"
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
          >
            Need changes? Contact us and we&apos;ll help you customize your website.
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default WebsitePreview;

